import React, { useState } from "react";
import { Button } from "react-bootstrap";
import { useHistory, useLocation } from "react-router-dom";
import styled, { useTheme } from "styled-components";
import * as config from "config";
import { withApiContext } from "context";
import { OverlayTooltip } from "components";
import { IconRubricsStart } from "Icons";

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: center;
  width: 100%;
  min-height: 70vh;
  padding: 40px 16px 80px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between; /* ensures title left, actions right */
  align-self: stretch;
  width: 100%;
  margin-bottom: 20px;
`;

const Divider = styled.hr`
  width: 100%;
  border: none;
  border-top: 1px solid ${({ theme }) => "#b4b2b2ff"};
  margin: 0 0 22px;
`;

const Title = styled.h1`
  font-size: 20px;
  line-height: 32px;
  margin: 0;
  font-weight: 600;
  color: ${({ theme }) => theme.color3};
  text-align: left;
`;

const Actions = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  justify-content: flex-end;
`;


const PrimaryButton = styled(Button)`
  && {
    min-width: 120px;
    border-radius: 999px;
    font-weight: 600;
    background: ${({ theme }) => theme.primary};
    border-color: ${({ theme }) => theme.primary};
    color: ${({ theme }) => theme.button?.primary?.text || "#ffffff"};
  }

  &&:hover,
  &&:focus,
  &&:active {
    background: ${({ theme }) =>
      theme.button?.primary?.backgroundHover || theme.primaryDarken || theme.primary};
    border-color: ${({ theme }) =>
      theme.button?.primary?.backgroundHover || theme.primaryDarken || theme.primary};
  }
`;

const SecondaryButton = styled(Button)`
  && {
    min-width: 120px;
    border-radius: 999px;
    font-weight: 600;
    background: ${({ theme }) => theme.color7 || "#ffffff"};
    border: 1px solid ${({ theme }) => theme.primary};
    color: ${({ theme }) => theme.primary};
  }

  &&:hover,
  &&:focus,
  &&:active {
    background: ${({ theme }) => theme.color6 || "#E8EEF0"};
    border-color: ${({ theme }) => theme.primary};
    color: ${({ theme }) => theme.primary};
  }
`;

const Illustration = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 320px;
  height: 320px;
  margin: 24px auto 40px;
`;

const StatsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 16px;
  margin-bottom: 12px;
`;

const StatCard = styled.div`
  flex: 1 1 180px;
  max-width: 220px;
  border: 1px solid ${({ theme }) => theme.primary};
  border-radius: 8px;
  padding: 12px 18px;
  display: flex;
  align-items: center;
  gap: 12px;
  background: ${({ theme }) => theme.color7 || "#ffffff"};
`;

const StatIcon = styled.span`
  font-size: 22px;
  color: ${({ theme }) => theme.primary};
`;

const StatContent = styled.div`
  display: flex;
  flex-direction: column;
  line-height: 1.2;
`;

const StatValue = styled.span`
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme.color2 || "#001B4A"};
`;

const StatLabel = styled.span`
  font-size: 14px;
  color: ${({ theme }) => theme.color3 || "#4A4A4A"};
`;

const GuidelinesMessage = styled.p`
  text-align: center;
  margin: 16px auto 0;
  color: ${({ theme }) => theme.color2 || "#001B4A"};
  font-size: 15px;
`;

const Subtitle = styled.p`
  margin: 0;
  font-size: 18px;
  color: ${({ theme }) => theme.color1 || "#272D36"};
  text-align: center;
`;

const Details = styled.div`
  text-align: center;
  margin-top: 16px;
  color: ${({ theme }) => theme.color2 || "#001B4A"};
  font-size: 16px;
`;

const ClipboardIllustration = ({ stroke, fill }) => (
  <svg width="260" height="260" viewBox="0 0 260 260" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect
      x="54"
      y="36"
      width="152"
      height="192"
      rx="20"
      stroke={stroke}
      strokeWidth="8"
      fill={fill}
    />
    <rect
      x="84"
      y="18"
      width="92"
      height="48"
      rx="16"
      stroke={stroke}
      strokeWidth="8"
      fill={fill}
    />
    <path
      d="M130 48H167"
      stroke={stroke}
      strokeWidth="8"
      strokeLinecap="round"
    />
    <path
      d="M95 108H165"
      stroke={stroke}
      strokeWidth="8"
      strokeLinecap="round"
    />
    <path
      d="M95 150H165"
      stroke={stroke}
      strokeWidth="8"
      strokeLinecap="round"
    />
    <path
      d="M95 192H165"
      stroke={stroke}
      strokeWidth="8"
      strokeLinecap="round"
    />
    <circle cx="79" cy="108" r="16" stroke={stroke} strokeWidth="8" fill={fill} />
    <circle cx="79" cy="150" r="16" stroke={stroke} strokeWidth="8" fill={fill} />
    <circle cx="79" cy="192" r="16" stroke={stroke} strokeWidth="8" fill={fill} />
    <path
      d="M70 102L79 111L89 99"
      stroke={stroke}
      strokeWidth="7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M70 186L79 195L89 183"
      stroke={stroke}
      strokeWidth="7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M70 144L88 162"
      stroke={stroke}
      strokeWidth="7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M88 144L70 162"
      stroke={stroke}
      strokeWidth="7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const StudentRubricDetails = (props) => {
  const { ctx_api } = props;
  const history = useHistory();
  const location = useLocation();
  const theme = useTheme();
  const [isStarting, setIsStarting] = useState(false);

  const rubrics = location.state?.rubrics;
  const subtitle = rubrics?.name || "Get ready to begin your rubric assessment.";
console.log(rubrics)
  const ensureNumber = (value) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : 0;
  };

  const questionMarksTotal = Array.isArray(rubrics?.questions)
    ? rubrics.questions.reduce(
        (acc, question) =>
          acc + ensureNumber(question?.total_marks ?? question?.marks ?? 0),
        0
      )
    : 0;
  const descriptions = rubrics.description
  const marksValue = questionMarksTotal 
  const questionValue = rubrics?.questions.length;
  const durationValue =
    rubrics?.time_limit_minutes ??
    0;

  const stats = [
    {
      key: "marks",
      icon: "🗒️",
      label: "Marks",
      value: ensureNumber(marksValue).toFixed(2),
    },
    {
      key: "questions",
      icon: "❓",
      label: "Questions",
      value: Math.max(0, Math.round(ensureNumber(questionValue))).toString(),
    },
    // {
    //   key: "minutes",
    //   icon: "⏱️",
    //   label: "Minutes",
    //   value: Math.max(0, Math.round(ensureNumber(durationValue))).toString(),
    // },
  ];

  const guidelinesText =
    descriptions ||
    rubrics?.guidelines ||
    rubrics?.instructions ||
    "There are no guidelines or instructions available for this assessment.";

  // Check if start date has been reached
  const isStartDateReached = () => {
    if (!rubrics) return false;
    // action_date = 1 means start date is reached (from API)
    if (rubrics.action_date === 1) return true;
    // Fallback: check from_date_original
    if (rubrics.from_date_original) {
      return new Date() >= new Date(rubrics.from_date_original);
    }
    return true; // Default to enabled if no date info
  };

  const canStart = isStartDateReached();

  const formatStartDate = () => {
    if (!rubrics?.from_date_original && !rubrics?.start_date) return "";
    const dateStr = rubrics.from_date_original || rubrics.start_date;
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return rubrics.start_date || dateStr;
    const options = { 
      weekday: 'short', 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    };
    return d.toLocaleString('en-US', options);
  };

  const startDateTooltip = !canStart ? `Assessment starts on ${formatStartDate()}` : "";

  const handleStart = async () => {
    if (isStarting) return;

    const rubricAssessmentId =
      rubrics?.id ?? rubrics?.rubrics?.id ?? null;

    if (!rubricAssessmentId) {
      history.push({
        pathname: config.ROUTES.StudentRubricAttempt,
        state: {
          rubrics,
          fromSurvey: location.state?.fromSurvey,
          from: location.state?.from || "rubrics",
          filter: location.state?.filter,
        },
      });
      return;
    }

    setIsStarting(true);
    try {
      await ctx_api.privateRequest({
        cmd: "insert_rubrics_assessment_start_time",
        args: {
          rubric_assessment_id: String(rubricAssessmentId),
          start_time: new Date().toISOString(),
        },
      });
    } catch (err) {
      console.error("Failed to store start time", err);
    } finally {
      setIsStarting(false);
      history.push({
        pathname: config.ROUTES.StudentRubricAttempt,
        state: {
          rubrics,
          fromSurvey: location.state?.fromSurvey,
          from: location.state?.from || "rubrics",
          filter: location.state?.filter,
        },
      });
    }
  };

  const handleExit = () => {
    history.push(config.ROUTES.StudentRubrics);
  };

  return (
    <Wrapper>
        <Header>
          <Title>{subtitle}</Title>
          <Actions>
            <SecondaryButton onClick={handleExit} variant="light">
              Exit
            </SecondaryButton>
            <OverlayTooltip
              tip={startDateTooltip}
              placement="top"
              disabled={canStart}
            >
              <span>
                <PrimaryButton 
                  onClick={handleStart} 
                  disabled={!rubrics || isStarting || !canStart}
                  style={!canStart ? { pointerEvents: 'none' } : {}}
                >
                 {rubrics?.rubric_status === "In Progress" ? "Resume" : "Start"}

                </PrimaryButton>
              </span>
            </OverlayTooltip>
          </Actions>
        </Header>
        <Divider />
        <Illustration>
          <IconRubricsStart />
        </Illustration>
        <StatsRow>
          {stats.map(({ key, icon, label, value }) => (
            <StatCard key={key}>
              <StatIcon aria-hidden>{icon}</StatIcon>
              <StatContent>
                <StatValue>{value}</StatValue>
                <StatLabel>{label}</StatLabel>
              </StatContent>
            </StatCard>
          ))}
        </StatsRow>
        <GuidelinesMessage  dangerouslySetInnerHTML={{ __html: guidelinesText }}></GuidelinesMessage>
        {/* <Subtitle></Subtitle> */}
        {/* {survey?.description && <Details>{survey.description}</Details>} */}
      </Wrapper>
  );
};

export default withApiContext(StudentRubricDetails);

